import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';

export type AddParticipantBody =
  | { participantType: 'CLIENT'; clientId: string }
  | { participantType: 'USER'; userId: string }
  | { participantType: 'EXTERNAL'; name: string; description: string };

export const useFamilyMeetingParticipants = (familyId: string) => {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['families', familyId, 'calendar-events'] });

  const basePath = (eventId: string) =>
    `families/${familyId}/calendar-events/${eventId}/participants`;

  const addParticipant = useMutation<
    { data: CalendarEvent },
    Error,
    { eventId: string; body: AddParticipantBody }
  >({
    mutationFn: ({ eventId, body }) => api.post<CalendarEvent>(basePath(eventId), body),
    onSuccess: invalidate,
  });

  const removeParticipant = useMutation<unknown, Error, { eventId: string; participantId: string }>(
    {
      mutationFn: ({ eventId, participantId }) =>
        api.delete(`${basePath(eventId)}/${participantId}`),
      onSuccess: invalidate,
    },
  );

  return { addParticipant, removeParticipant };
};
